import { FC, useCallback } from 'react'

import { WorkspaceActionPopup } from 'components/pages/Workspace/WorkspaceActionPopup'
import { WorkspaceUserProfile } from 'models/user'
import { useQueryClient } from '@tanstack/react-query'
import { EntityKeys } from 'utils/reactQuery'
import { useLeaveWorkspace } from 'dataQueries'
import { logger } from 'utils/logging'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

interface Props {
  /** Whether the modal is open */
  isOpen: boolean
  /** The workspace the user wants to leave */
  workspace: WorkspaceUserProfile
  /** Callback to close the modal and reset state */
  onClose: () => void
}

export const LeaveWorkspacePopup: FC<Props> = ({
  isOpen,
  workspace,
  onClose,
}) => {
  const { t } = useTranslation(['user_workspace_list', 'common'])
  const queryClient = useQueryClient()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const leaveWorkspace = useLeaveWorkspace()

  const handleClose = useCallback(() => {
    leaveWorkspace.reset()
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .reset is stable
  }, [leaveWorkspace.reset, onClose])

  const handleConfirm = useCallback(() => {
    leaveWorkspace.mutate({ workspaceId: workspace.id }, {
      onSuccess: () => {
        // Only drop the selection when it is the workspace being left. Leaving some other workspace from
        // the profile list must not move the user out of the one they are working in, which clearing
        // unconditionally would do. When it IS the current one, resolution skips it anyway once the
        // membership is gone, so this only avoids a frame of showing a workspace already left.
        if (sessionStorage.getItem('workspaceId') === workspace.id) {
          sessionStorage.removeItem('workspaceId')
        }
        spawnSuccessToast(t('leave_workspace_success'))
        queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
        handleClose()
      },
      onError: (error) => {
        logger.error('Failed to leave workspace', error, { context: { component: 'LeaveWorkspacePopup', action: 'leaveWorkspace', metadata: { workspaceId: workspace.id } } })
        spawnErrorToast(t('leave_workspace_error'))
        leaveWorkspace.reset()
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate and .reset are stable
  }, [leaveWorkspace.mutate, leaveWorkspace.reset, workspace.id, spawnSuccessToast, spawnErrorToast, t, queryClient, handleClose])

  return (
    <WorkspaceActionPopup
      isOpen={isOpen}
      workspaceName={workspace.name}
      title={t('leave_workspace_title', { workspaceName: workspace.name })}
      warningTitle={t('leave_workspace_warning_title')}
      warningDescription={t('leave_workspace_warning_description')}
      inputLabel={t('leave_workspace_input_label')}
      isPending={leaveWorkspace.isPending}
      onClose={handleClose}
      onConfirm={handleConfirm}
    />
  )
}
